const defineModule = (m) => m;
/**
 * The module catalogue. Adding a feature = adding a manifest here and a NestJS module that registers it.
 * `status` flips from 'planned' to 'available' as each phase ships.
 */
export const MODULE_CATALOG = [
    defineModule({
        key: 'administration',
        name: 'Administration',
        description: 'Users, roles, organisation settings and the audit log.',
        milestone: 'core',
        requirements: ['M18-07', 'M18-08'],
        dependsOn: [],
        alwaysOn: true,
        status: 'available',
        permissions: ['org.settings.manage', 'users.read', 'users.manage', 'roles.manage', 'audit.read'],
        nav: [
            {
                key: 'users',
                label: 'Users & Roles',
                path: '/admin/users',
                icon: 'users',
                group: 'admin',
                permission: 'users.read',
                order: 1,
            },
            {
                key: 'settings',
                label: 'Organisation',
                path: '/admin/settings',
                icon: 'building-2',
                group: 'admin',
                permission: 'org.settings.manage',
                order: 2,
            },
            {
                key: 'audit',
                label: 'Audit Log',
                path: '/admin/audit',
                icon: 'scroll-text',
                group: 'admin',
                permission: 'audit.read',
                order: 9,
            },
        ],
    }),
    defineModule({
        key: 'compliance',
        name: 'Compliance Guard',
        description: 'Rule engine, suppression list, CEO flags panel and restricted exports.',
        milestone: 'core',
        requirements: ['M18-02', 'M18-03', 'M18-04', 'M7-05'],
        dependsOn: [],
        alwaysOn: true,
        status: 'planned',
        permissions: ['compliance.view', 'exports.run'],
        nav: [
            {
                key: 'compliance',
                label: 'Compliance',
                path: '/admin/compliance',
                icon: 'shield-check',
                group: 'admin',
                permission: 'compliance.view',
                order: 5,
            },
        ],
    }),
    defineModule({
        key: 'my-day',
        name: 'My Day',
        description: 'Ready day plan on login, driven by the playbook.',
        milestone: 'A',
        requirements: ['M1-01', 'M1-02', 'M1-03', 'M1-09'],
        dependsOn: ['playbook', 'pipeline'],
        alwaysOn: false,
        status: 'planned',
        permissions: ['myday.use', 'myday.view_others'],
        nav: [
            {
                key: 'my-day',
                label: 'My Day',
                path: '/my-day',
                icon: 'sun',
                group: 'work',
                permission: 'myday.use',
                order: 1,
            },
        ],
    }),
    defineModule({
        key: 'pipeline',
        name: 'Pipeline & Revenue',
        description: 'Accounts, contacts, the seven-stage board, potential revenue and targets.',
        milestone: 'A',
        requirements: ['M3-01', 'M3-11', 'M16-01', 'M16-07'],
        dependsOn: ['playbook'],
        alwaysOn: false,
        status: 'available',
        permissions: ['pipeline.read', 'pipeline.write', 'targets.manage'],
        nav: [
            {
                key: 'pipeline',
                label: 'Pipeline',
                path: '/pipeline',
                icon: 'kanban',
                group: 'work',
                permission: 'pipeline.read',
                order: 2,
            },
            {
                key: 'accounts',
                label: 'Accounts',
                path: '/accounts',
                icon: 'building',
                group: 'work',
                permission: 'pipeline.read',
                order: 3,
            },
            {
                key: 'targets',
                label: 'Targets',
                path: '/setup/targets',
                icon: 'flag',
                group: 'setup',
                permission: 'pipeline.read',
                order: 6,
            },
        ],
    }),
    defineModule({
        key: 'touches',
        name: 'Touch Logging',
        description: 'Two-click logging of every outreach and reply.',
        milestone: 'A',
        requirements: ['M4-01', 'M4-07'],
        dependsOn: ['pipeline'],
        alwaysOn: false,
        status: 'available',
        permissions: ['touches.write'],
        nav: [],
    }),
    defineModule({
        key: 'import',
        name: 'CSV Import',
        description: 'Move Excel data in, with validation, de-duplication and rollback.',
        milestone: 'A',
        requirements: ['M2-01', 'M2-08'],
        dependsOn: ['pipeline', 'touches'],
        alwaysOn: false,
        status: 'available',
        permissions: ['import.run'],
        nav: [
            {
                key: 'import',
                label: 'Import',
                path: '/setup/import',
                icon: 'upload',
                group: 'setup',
                permission: 'import.run',
                order: 4,
            },
        ],
    }),
    defineModule({
        key: 'playbook',
        name: 'Playbook & Setup',
        description: 'ICPs, services, pitches, cadence rules, quotas and thresholds.',
        milestone: 'A',
        requirements: ['M5-01', 'M5-06', 'M5-09'],
        dependsOn: [],
        alwaysOn: false,
        status: 'available',
        permissions: ['playbook.read', 'playbook.manage'],
        nav: [
            {
                key: 'playbook',
                label: 'Playbook',
                path: '/setup/playbook',
                icon: 'sliders-horizontal',
                group: 'setup',
                permission: 'playbook.read',
                order: 1,
            },
            {
                key: 'icps',
                label: 'ICPs & Pitches',
                path: '/setup/icps',
                icon: 'target',
                group: 'setup',
                permission: 'playbook.read',
                order: 2,
            },
            {
                key: 'lists',
                label: 'Lists & Services',
                path: '/setup/lists',
                icon: 'list',
                group: 'setup',
                permission: 'playbook.read',
                order: 5,
            },
        ],
    }),
    defineModule({
        key: 'library',
        name: 'Library',
        description: "Cheat sheets, checklists, Do's and Don'ts with contextual links.",
        milestone: 'A',
        requirements: ['M5-07'],
        dependsOn: [],
        alwaysOn: false,
        status: 'available',
        permissions: ['library.read', 'library.manage'],
        nav: [
            {
                key: 'library',
                label: 'Library',
                path: '/setup/library',
                icon: 'book-open',
                group: 'setup',
                permission: 'library.read',
                order: 3,
            },
        ],
    }),
    defineModule({
        key: 'dashboards',
        name: 'Dashboards & RAG',
        description: 'Live dashboards for BDM and CEO, with RAG at four levels.',
        milestone: 'A',
        requirements: ['M6-01', 'M7-01', 'M7-06'],
        dependsOn: ['pipeline'],
        alwaysOn: false,
        status: 'planned',
        permissions: ['dashboards.read'],
        nav: [
            {
                key: 'dashboards',
                label: 'Dashboards',
                path: '/insights/dashboards',
                icon: 'layout-dashboard',
                group: 'insights',
                permission: 'dashboards.read',
                order: 1,
            },
        ],
    }),
    defineModule({
        key: 'alerts',
        name: 'Alerts',
        description: 'Early warnings for leads, activity and pipeline health.',
        milestone: 'A',
        requirements: ['M17-01', 'M17-04'],
        dependsOn: [],
        alwaysOn: false,
        status: 'planned',
        permissions: ['alerts.read'],
        nav: [
            {
                key: 'alerts',
                label: 'Alerts',
                path: '/insights/alerts',
                icon: 'bell',
                group: 'insights',
                permission: 'alerts.read',
                order: 3,
            },
        ],
    }),
    defineModule({
        key: 'automation',
        name: 'Automation Planner',
        description: 'Every scheduled automation with pause, resume and run history.',
        milestone: 'B',
        requirements: ['M8-03', 'M8-04', 'M8-05'],
        dependsOn: [],
        alwaysOn: false,
        status: 'planned',
        permissions: ['automation.manage'],
        nav: [
            {
                key: 'automation',
                label: 'Automation',
                path: '/admin/automation',
                icon: 'workflow',
                group: 'admin',
                permission: 'automation.manage',
                order: 3,
            },
        ],
    }),
    defineModule({
        key: 'email',
        name: 'Email Automation',
        description: 'Templates, sequences, approval queue and deliverability safeguards.',
        milestone: 'B',
        requirements: ['M9-01', 'M9-11'],
        dependsOn: ['pipeline', 'playbook'],
        alwaysOn: false,
        status: 'planned',
        permissions: ['email.approve', 'email.templates.manage'],
        nav: [
            {
                key: 'email-queue',
                label: 'Email Queue',
                path: '/work/email-queue',
                icon: 'mail',
                group: 'work',
                permission: 'email.approve',
                order: 4,
            },
        ],
    }),
    defineModule({
        key: 'performance',
        name: 'Effort & Performance',
        description: 'Time entry, consistency checks and the BDM performance indicator.',
        milestone: 'B',
        requirements: ['M14-01', 'M14-07', 'M15-01', 'M15-05'],
        dependsOn: ['my-day'],
        alwaysOn: false,
        status: 'planned',
        permissions: ['performance.read', 'flags.view', 'effort.write'],
        nav: [
            {
                key: 'performance',
                label: 'Performance',
                path: '/insights/performance',
                icon: 'gauge',
                group: 'insights',
                permission: 'performance.read',
                order: 2,
            },
        ],
    }),
    defineModule({
        key: 'ai-insights',
        name: 'AI Insights & Reports',
        description: 'Scheduled pipeline analyzer, day-end and weekend reports via the AI Gateway.',
        milestone: 'C',
        requirements: ['M10-01', 'M11-01', 'M12-01'],
        dependsOn: ['pipeline'],
        alwaysOn: false,
        status: 'planned',
        permissions: ['ai.read', 'ai.profile.manage'],
        nav: [
            {
                key: 'reports',
                label: 'Reports',
                path: '/insights/reports',
                icon: 'file-bar-chart',
                group: 'insights',
                permission: 'ai.read',
                order: 4,
            },
            {
                key: 'ai',
                label: 'AI Settings',
                path: '/admin/ai',
                icon: 'sparkles',
                group: 'admin',
                permission: 'ai.profile.manage',
                order: 4,
            },
        ],
    }),
    defineModule({
        key: 'lookalike',
        name: 'Lookalike Search',
        description: 'Similar companies for Green ICPs, for BDM review.',
        milestone: 'C',
        requirements: ['M13-01', 'M13-06'],
        dependsOn: ['ai-insights', 'pipeline'],
        alwaysOn: false,
        status: 'planned',
        permissions: ['lookalike.review'],
        nav: [
            {
                key: 'lookalike',
                label: 'Lookalikes',
                path: '/work/lookalikes',
                icon: 'radar',
                group: 'work',
                permission: 'lookalike.review',
                order: 5,
            },
        ],
    }),
];
export const MODULE_KEYS = MODULE_CATALOG.map((m) => m.key);
export function findModule(key) {
    return MODULE_CATALOG.find((m) => m.key === key);
}
//# sourceMappingURL=catalog.js.map